import { useState, useCallback } from 'react';
import { useAdmin } from '@/hooks/useAdmin';

const Q_KEY        = 'hpe_free_questions_used';
const Q_TS_KEY     = 'hpe_free_questions_timestamp';   // ISO timestamp of when instant questions were used up
const DAILY_TS_KEY = 'hpe_daily_question_used_at';     // ISO timestamp of the last daily question use
const CR_KEY       = 'hpe_free_client_record_used';

const INSTANT_Q_LIMIT = 1;      // single free question on first visit
const FREE_CR_LIMIT   = 1;
const DAILY_MS        = 24 * 60 * 60 * 1000;   // 24 hours in ms

/** Returns ms remaining until the next free daily question (0 = available now) */
function msUntilDailyQuestion(): number {
  const raw = localStorage.getItem(DAILY_TS_KEY);
  if (!raw) return 0;
  const elapsed = Date.now() - new Date(raw).getTime();
  return Math.max(0, DAILY_MS - elapsed);
}

/** Formats ms remaining as "Xh Ym" */
export function formatCountdown(ms: number): string {
  if (ms <= 0) return 'now';
  const totalMins = Math.ceil(ms / 60_000);
  const h = Math.floor(totalMins / 60);
  const m = totalMins % 60;
  if (h > 0 && m > 0) return `${h}h ${m}m`;
  if (h > 0) return `${h}h`;
  return `${m}m`;
}

export function useFreeUsage() {
  const { isAdmin } = useAdmin();
  const [questionCount, setQuestionCount] = useState<number>(() => {
    const stored = localStorage.getItem(Q_KEY);
    return stored ? parseInt(stored, 10) : 0;
  });

  const [clientRecordUsed, setClientRecordUsed] = useState<boolean>(() => {
    return localStorage.getItem(CR_KEY) === 'true';
  });

  // ── Derived state ──────────────────────────────────────────────────────────
  // Admins bypass all limits. Otherwise: HARD CAP at INSTANT_Q_LIMIT (no daily reset).
  const instantQuestionsExhausted = isAdmin ? false : questionCount >= INSTANT_Q_LIMIT;

  // Daily reset is disabled — once the 2 free questions are used, the user must upgrade.
  const dailyMsRemaining = 0;
  const dailyQuestionAvailable = false;

  const hasReachedQuestionLimit = isAdmin ? false : instantQuestionsExhausted;

  const freeQuestionsRemaining = isAdmin ? 999 : Math.max(0, INSTANT_Q_LIMIT - questionCount);

  const showOneQuestionRemainingNotice = isAdmin ? false : questionCount === 1;

  // ── Actions ────────────────────────────────────────────────────────────────
  const incrementFreeQuestion = useCallback(() => {
    setQuestionCount((prev) => {
      const next = prev + 1;
      localStorage.setItem(Q_KEY, String(next));
      // Record when the instant quota was used up so we can start daily tracking
      if (next >= INSTANT_Q_LIMIT && !localStorage.getItem(Q_TS_KEY)) {
        localStorage.setItem(Q_TS_KEY, new Date().toISOString());
      }
      return next;
    });
  }, []);

  /**
   * Called when a daily free question is consumed (after sign-in).
   * Stamps the current time so the 24h window begins.
   */
  const consumeDailyQuestion = useCallback(() => {
    localStorage.setItem(DAILY_TS_KEY, new Date().toISOString());
  }, []);

  // ── Client Record ──────────────────────────────────────────────────────────
  const hasUsedFreeClientRecord = isAdmin ? false : clientRecordUsed;

  const markFreeClientRecordUsed = useCallback(() => {
    localStorage.setItem(CR_KEY, 'true');
    setClientRecordUsed(true);
  }, []);

  return {
    // Admin bypass
    isAdmin,
    // Questions
    freeQuestionsUsed: questionCount,
    freeQuestionsRemaining,
    hasReachedQuestionLimit,
    showOneQuestionRemainingNotice,
    instantQuestionsExhausted,
    dailyQuestionAvailable,
    dailyMsRemaining,
    incrementFreeQuestion,
    consumeDailyQuestion,
    // Client Records
    hasUsedFreeClientRecord,
    markFreeClientRecordUsed,
    freeClientRecordLimit: FREE_CR_LIMIT,
  };
}
